import React from 'react';
import { Box, Button, ButtonBase, Checkbox, Chip, Paper, Typography } from '@mui/material';
import { isSelectable } from './BulkBar';
import { tint } from './lib/tone';
import { Bot, Company, Snapshot } from './lib/types';
import { ActionRow, AcFilters, BUCKETS, Bucket, DECISION_FILTERS, DecisionFilter, IMPL_LABEL, ImplFilter, applyAcFilters, areaCounts, areaNames, bucketCounts, decisionCounts, teamCounts } from './lib/selectors';
import { ImpactTiles } from './ImpactTiles';
import { EXEC_ORDER } from './lib/types';
import { fmtIct } from './lib/ict';
import { DecisionStateChip, GaChip, NoReviewChip, PriorityChip, ProvLine, RecommendedLabel, UnreadBadge, VerdictChip } from './chips';
import { ACCENT, FOCUS_RING, FONT, WARN_TEXT, OVERLINE, RADIUS, SHADOW, TONE } from './lib/brand';

const FIRST_PAGE = 15;
const PAGE = 25;
const LABEL: Record<DecisionFilter, string> = { all: 'All', pending: 'Pending', approved: 'Approved', declined: 'Declined', instructed: 'Instructed', blockers: 'Blockers' };

/** One row of filter pills: "All …" plus one pill per value, each with the count it would show. */
function PillRow({ label, allLabel, values, value, counts, total, onChange, testId }: {
  label: string; allLabel: string; values: string[]; value: string | null; counts: Record<string, number>; total: number;
  onChange: (v: string | null) => void; testId: string;
}) {
  return (
    <Box
      role="group"
      aria-label={`Filter by ${label}`}
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.9, flexWrap: { xs: 'nowrap', md: 'wrap' }, overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.5, md: 0 } }}
    >
      <Typography component="span" sx={{ ...OVERLINE, fontSize: 10, minWidth: 58, flexShrink: 0 }}>{label}</Typography>
      {[null, ...values].map((v) => {
        const on = value === v;
        const n = v === null ? total : counts[v] || 0;
        // "Leadership & Implementation (reports to CEO)" reads as "Leadership & Implementation"; the title keeps the full name.
        const shown = v === null ? allLabel : v.replace(/\s*\(reports to [^)]*\)$/i, '');
        return (
          <Chip
            key={v || '__all'}
            clickable
            size="small"
            onClick={() => onChange(on && v !== null ? null : v)}
            aria-pressed={on}
            label={`${shown} ${n}`}
            title={v || undefined}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            sx={{ flexShrink: 0, opacity: !on && n === 0 ? 0.55 : 1 }}
          />
        );
      })}
    </Box>
  );
}

/** Renders the decision cell. Phase 1 is read-only: the state chip, or "Pending decision". */
export interface DecisionCellProps { row: ActionRow }

function Row({
  row, accent, highlighted, onOpenAction, onOpenBot, onGa, renderDecision, unread, selectable, checked, onToggleSelect, onWhy,
}: {
  row: ActionRow; accent: string; highlighted: boolean;
  onOpenAction: (id: string) => void; onOpenBot: (b: Bot) => void; onGa: (id: string) => void;
  renderDecision: (p: DecisionCellProps) => React.ReactNode; unread: number;
  selectable: boolean; checked: boolean; onToggleSelect?: (id: string) => void; onWhy?: (row: ActionRow) => void;
}) {
  const { action, bot } = row;
  const state = action.decision && action.decision.value;
  const review = action.review;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Box
      role="group"
      tabIndex={0}
      data-testid={`action-${action.id}`}
      data-action-row={action.id}
      data-selected={checked ? 'true' : undefined}
      aria-label={`${action.for || action.text || 'Item'}. Open the recommendation`}
      onClick={() => onOpenAction(action.id)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenAction(action.id); }
      }}
      sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '96px minmax(0,1fr) minmax(0,1.3fr) 190px 200px' }, gap: 1.5,
        px: 2, py: 1.4, borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer', textAlign: 'left',
        bgcolor: highlighted ? tint(TONE.warn.bg) : state === 'yes' ? tint(TONE.good.bg) : state === 'no' ? tint(TONE.neutral.bg) : 'transparent',
        opacity: state === 'no' ? 0.8 : 1,
        '&:hover': { bgcolor: 'action.hover' }, '&:focus-visible': { outline: FOCUS_RING, outlineOffset: -3 },
      }}
    >
      <Box onClick={stop}>
        <PriorityChip priority={action.priority} />
        {selectable && onToggleSelect && (
          <Checkbox
            size="small"
            checked={checked}
            onChange={() => onToggleSelect(action.id)}
            inputProps={{ 'aria-label': `Select "${action.for || action.text || 'item'}" for a bulk decision` }}
            sx={{ display: 'block', p: 0.3, mt: 0.5, ml: -0.3 }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{action.for || action.text || 'Untitled item'}</Typography>
        <Box sx={{ mt: 0.3 }}><ImpactTiles action={action} compact /></Box>
        {action.bot_original && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1, my: 0.5 }}>
            Bot proposed: {action.bot_original.length > 260 ? `${action.bot_original.slice(0, 260)}…` : action.bot_original}
          </Typography>
        )}
        {review && review.grant_note && review.grant_note.text && (
          <Typography variant="caption" component="div" sx={{ color: WARN_TEXT, my: 0.4 }}>⚑ {review.grant_note.text}</Typography>
        )}
        {review && (review.ga || []).length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', my: 0.5 }} onClick={stop}>
            {(review.ga || []).map((g) => <GaChip key={g.id} ga={g} onClick={onGa} />)}
          </Box>
        )}
        <Box sx={{ mt: 0.3 }}><ProvLine prov={action.prov} fmt={fmtIct} /></Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <RecommendedLabel action={action} accent={accent} />
        <Typography sx={{ fontSize: 13, mt: 0.6 }}>{action.recommended_action || 'Not stated in source'}</Typography>
        {action.recommended_prov && <Box sx={{ mt: 0.3 }}><ProvLine prov={action.recommended_prov} fmt={fmtIct} /></Box>}
      </Box>

      <Box sx={{ minWidth: 0 }} onClick={stop}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <ButtonBase onClick={() => onOpenBot(bot)} sx={{ fontSize: 12.5, fontWeight: 700, textAlign: 'left', color: 'primary.main' }}>{bot.name}</ButtonBase>
          <UnreadBadge count={unread} />
        </Box>
        <Box sx={{ my: 0.6 }}>
          {review
            ? <VerdictChip exec={review.exec} verdict={review.verdict} onClick={() => onOpenAction(action.id)} />
            : <NoReviewChip level={bot.reviews && bot.reviews.length ? 'item' : 'bot'} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
          <ButtonBase onClick={() => onOpenAction(action.id)} sx={{ fontSize: 11.5, color: 'primary.main', textDecoration: 'underline' }}>Open · Ask a question</ButtonBase>
          {onWhy && <ButtonBase onClick={() => onWhy(row)} aria-label={`Why is "${action.for || action.text || 'this item'}" here?`} sx={{ fontSize: 11.5, color: 'primary.main', textDecoration: 'underline' }}>Why?</ButtonBase>}
        </Box>
      </Box>

      <Box onClick={stop} sx={{ minWidth: 0 }}>
        {renderDecision({ row })}
      </Box>
    </Box>
  );
}

/** Default Phase 1 decision cell: read-only. Phase 2 swaps in the Yes / No / Instruct controls. */
export const ReadOnlyDecision = ({ row }: DecisionCellProps) => {
  const { action } = row;
  if (!action.decidable) {
    return <Typography variant="caption" color="text.secondary">No decision: blocker, not a proposal. See the bot drawer.</Typography>;
  }
  if (action.decision && action.decision.value !== 'pending') {
    return (
      <Box>
        <DecisionStateChip value={action.decision.value} />
        {action.decision.decided_at && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.4 }}>
            {fmtIct(action.decision.decided_at)}{action.decision.decided_by ? ` · ${action.decision.decided_by}` : ''}
          </Typography>
        )}
      </Box>
    );
  }
  return <Typography variant="caption" color="text.secondary">Pending decision</Typography>;
};

export function ActionCenter({
  snapshot, company, rows, allRowsForCounts, decision, onDecisionFilter, selectedBot, onClearBot, onInstructBot, onOpenBotDetail,
  highlightId, onOpenAction, onOpenBot, onGa, renderDecision = ReadOnlyDecision, unread = {},
  selected, onToggleSelect, onWhy, bulkBar, team = null, area = null, onTeam, onArea,
  bucket = null, impl = null, onBucket, onImpl,
}: {
  snapshot: Snapshot; company: Company; rows: ActionRow[]; allRowsForCounts: ActionRow[];
  decision: DecisionFilter; onDecisionFilter: (d: DecisionFilter) => void;
  selectedBot: Bot | null; onClearBot: () => void; onInstructBot: (b: Bot) => void; onOpenBotDetail: (b: Bot) => void;
  highlightId?: string | null;
  onOpenAction: (id: string) => void; onOpenBot: (b: Bot) => void; onGa: (id: string) => void;
  renderDecision?: (p: DecisionCellProps) => React.ReactNode; unread?: Record<string, number>;
  /** Bulk selection (Grant only): ids chosen, the toggle, and the sticky bar to render under the rows. */
  selected?: Set<string>; onToggleSelect?: (id: string) => void; onWhy?: (row: ActionRow) => void; bulkBar?: React.ReactNode;
  /** C-suite line and org-chart area filters; the pill rows show only when their handlers are given. */
  team?: string | null; area?: string | null; onTeam?: (exec: string | null) => void; onArea?: (area: string | null) => void;
  /** Impact bucket and implementation status filters. */
  bucket?: Bucket | null; impl?: ImplFilter | null; onBucket?: (b: Bucket | null) => void; onImpl?: (i: ImplFilter | null) => void;
}) {
  const accent = ACCENT[company].acc;
  // The filters combine with AND. Each pill row's counts are taken with every other filter
  // applied, so every pill shows exactly how many items clicking it would leave.
  const areas = React.useMemo(() => areaNames(snapshot, company, rows), [snapshot, company, rows]);
  const areaOn = area && areas.includes(area) ? area : null;
  const f: AcFilters = { decision, team, area: areaOn, bucket, impl };
  const without = (k: keyof AcFilters) => applyAcFilters(rows, snapshot, f, k);
  const counts = decisionCounts(applyAcFilters(allRowsForCounts, snapshot, f, 'decision'));
  const tCounts = teamCounts(without('team'), snapshot);
  const aCounts = areaCounts(without('area'), areas);
  const bCounts = bucketCounts(without('bucket'));
  const buckets = BUCKETS.filter((b) => b !== 'Unbucketed' || bCounts[b] > 0 || bucket === b);
  const filtered = applyAcFilters(rows, snapshot, f);
  const [shown, setShown] = React.useState(FIRST_PAGE);
  // A change of filter, bot or company starts from the first page again.
  React.useEffect(() => { setShown(FIRST_PAGE); }, [decision, team, areaOn, bucket, impl, selectedBot && selectedBot.slug, company]);
  // A deep-linked item that is further down must be reachable.
  const hlIndex = highlightId ? filtered.findIndex((r) => r.action.id === highlightId) : -1;
  const visible = filtered.slice(0, Math.max(shown, hlIndex + 1));
  const left = filtered.length - visible.length;

  return (
    <Paper variant="outlined" component="section" id="bo-actions" aria-label="Action Center" data-testid="action-center" sx={{ borderRadius: RADIUS.xl, boxShadow: SHADOW.card, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ p: 2, pb: 1.2 }}>
        <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 700 }}>Action Center / Inbox</Typography>
        <Typography variant="caption" color="text.secondary">
          Yes / No records your decision and queues approved items for the Implementation Bot. Nothing executes from this page.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            {filtered.length} of {allRowsForCounts.length} items{selectedBot ? ` for ${selectedBot.short}` : ` across ${snapshot.bots.filter((b) => b.company === company).length} bots`}
          </Typography>
          {DECISION_FILTERS.map((f) => (
            <Chip
              key={f}
              clickable
              onClick={() => onDecisionFilter(f)}
              aria-pressed={decision === f}
              label={`${LABEL[f]} ${counts[f]}`}
              size="small"
              color={decision === f ? 'primary' : 'default'}
              variant={decision === f ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
        {onTeam && (
          <PillRow label="C-suite" allLabel="All execs" testId="team-filter" values={[...EXEC_ORDER]} value={team} counts={tCounts} total={without('team').length} onChange={onTeam} />
        )}
        {onArea && areas.length > 0 && (
          <PillRow label="Area" allLabel="All areas" testId="area-filter" values={areas} value={areaOn} counts={aCounts} total={without('area').length} onChange={onArea} />
        )}
        {onBucket && (
          <PillRow label="Impact" allLabel="All buckets" testId="bucket-filter" values={[...buckets]} value={bucket} counts={bCounts} total={without('bucket').length} onChange={(v) => onBucket(v as Bucket | null)} />
        )}
        {impl && (
          <Box sx={{ mt: 0.9 }}>
            <Chip size="small" color="primary" label={`Implementation: ${IMPL_LABEL[impl]}`} onDelete={onImpl ? () => onImpl(null) : undefined} data-testid="impl-filter" />
          </Box>
        )}
      </Box>

      {selectedBot && (
        <Box data-testid="selection-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1, bgcolor: tint(ACCENT[company].soft), borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{selectedBot.name}</Typography>
          <Typography variant="caption" color="text.secondary">{statusText(selectedBot)}</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" onClick={() => onInstructBot(selectedBot)}>Instruct this bot</Button>
          <Button size="small" variant="outlined" onClick={() => onOpenBotDetail(selectedBot)}>Open full detail</Button>
          <Button size="small" onClick={onClearBot}>Show all</Button>
        </Box>
      )}

      <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '96px minmax(0,1fr) minmax(0,1.3fr) 190px 200px', gap: 1.5, px: 2, py: 0.8, bgcolor: 'action.hover', position: 'sticky', top: 64, zIndex: 1 }}>
        {['Priority', 'Finding / item · source', 'Recommended action', 'Bot · C-suite verdict', 'Your decision'].map((h) => (
          <Typography key={h} variant="overline" sx={{ ...OVERLINE, fontSize: 10.5 }}>{h}</Typography>
        ))}
      </Box>

      <Box aria-live="polite">
        {visible.length === 0 ? (
          <Typography data-testid="action-empty" variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {selectedBot && allRowsForCounts.length === 0
              ? `No action items found in ${selectedBot.short}'s files.${selectedBot.latest_output && selectedBot.latest_output.title ? ` Latest output: ${selectedBot.latest_output.title}.` : ''}`
              : emptyText(decision, team, areaOn, bucket, impl)}
          </Typography>
        ) : visible.map((r) => (
          <Row
            key={r.action.id}
            row={r}
            accent={accent}
            highlighted={highlightId === r.action.id}
            onOpenAction={onOpenAction}
            onOpenBot={onOpenBot}
            onGa={onGa}
            renderDecision={renderDecision}
            unread={unread[r.bot.slug] || 0}
            selectable={Boolean(onToggleSelect) && isSelectable(r.action)}
            checked={Boolean(selected && selected.has(r.action.id))}
            onToggleSelect={onToggleSelect}
            onWhy={onWhy}
          />
        ))}
      </Box>

      {bulkBar}

      {left > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" size="small" onClick={() => setShown((n) => n + PAGE)}>
            Show {Math.min(PAGE, left)} more ({left} left)
          </Button>
        </Box>
      )}
    </Paper>
  );
}

function emptyText(decision: DecisionFilter, team: string | null, area: string | null, bucket: Bucket | null = null, impl: ImplFilter | null = null): string {
  const what = decision === 'all' ? 'items' : decision === 'blockers' ? 'blockers' : `${LABEL[decision].toLowerCase()} items`;
  const who = [team ? `for ${team}` : '', area ? `in ${area}` : '', bucket ? `in the ${bucket} bucket` : '', impl ? `with implementation ${IMPL_LABEL[impl].toLowerCase()}` : ''].filter(Boolean).join(' ');
  return who ? `No ${what} ${who}.` : `No items match the "${LABEL[decision]}" filter.`;
}

function statusText(b: Bot): string {
  const s = b.color === 'green' ? 'Active' : b.color === 'yellow' ? 'Pending decision' : 'Idle';
  return `${s} · last activity ${fmtIct(b.last_activity_ict)}`;
}
